import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    getAreaListThunk,
    getEmployeePositionGetCurrentPositionThunk,
    getNetworkInfoThunk,
    getUserMeThunk,
    patchUserUpdateThunk,
    postUserLoginThunk,
} from "tm-shared/api";
import { UserRole } from "tm-shared/types";

import { ALLOW_APP_ROLES } from "@/config";

import { UserState } from "./user.slice.types";

const initialState: UserState = {
    loggedInAt: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: null,
    id: "",
    roleName: "",
    role: (localStorage.getItem("role") as UserRole) ?? null,
    availableRoles: [],
    regions: {},
    networks: {},
    route: null,
    employee: null,
    photo: null,
    isExternal: false,
};

const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
        setLoggedInAt(state, action: PayloadAction<string>) {
            state.loggedInAt = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder

            .addCase(getUserMeThunk.fulfilled, (state, action) => {
                const {
                    firstName,
                    lastName,
                    phone,
                    email,
                    userId,
                    authorizedRole,
                    photo,
                    roles,
                    isExternal,
                } = action.payload.result;

                state.firstName = firstName;
                state.lastName = lastName;
                state.phone = phone;
                state.email = email;
                state.id = userId;
                state.roleName = authorizedRole?.name ?? "";
                state.role = authorizedRole?.slug as UserRole;
                state.photo = photo;
                state.isExternal = isExternal;

                localStorage.setItem("userId", userId);

                state.availableRoles = roles
                    .filter((role) => ALLOW_APP_ROLES.includes(role.slug))
                    .map(({ name, slug }) => ({
                        label: name,
                        value: slug,
                    }));
            })

            .addCase(
                getEmployeePositionGetCurrentPositionThunk.fulfilled,
                (state, action) => {
                    const { selfRoute, ...rest } = action.payload.result;

                    state.route = selfRoute;
                    state.employee = rest;
                },
            )

            .addCase(getAreaListThunk.fulfilled, (state, action) => {
                state.regions = action.payload.result.reduce(
                    (acc, region) => {
                        acc[region.id] = region.name;
                        return acc;
                    },
                    {} as Record<string, string>,
                );
            })
            .addCase(getNetworkInfoThunk.fulfilled, (state, action) => {
                state.networks = {
                    ...state.networks,
                    [action.payload.result.id]: action.payload.result.name,
                };
            })
            .addCase(postUserLoginThunk.fulfilled, (state, action) => {
                state.role = action.meta.arg.asRole ?? null;
            })
            .addCase(patchUserUpdateThunk.fulfilled, (state, { meta }) => {
                state.photo = meta.arg.photo;
            });
    },
});

export const { setLoggedInAt } = userSlice.actions;

export default userSlice.reducer;
